import clsx from 'clsx';
import { Plus } from 'lucide-react';
import s from './FaqAccordion.module.css';

/**
 * FaqAccordion — the single, SEO-friendly FAQ accordion used site-wide.
 *
 * Built on native <details>/<summary>, so every answer is server-rendered and
 * ALWAYS in the DOM (crawlable by search engines) — unlike JS accordions that
 * unmount collapsed content. Works without JS; keyboard + screen-reader
 * accessible via native semantics.
 *
 * Props:
 *   items   - [{ q, a }] (required). q/a are already-resolved strings/nodes
 *             (resolve i18n at the call site, e.g. t(key)).
 *   columns - 1 (default) | 2 — "2" renders a responsive two-column grid
 *             (stacks to one column under lg), matching the homepage layout.
 *   name    - optional <details name> group so only one item opens at a time
 *             (native exclusive accordion; degrades to multi-open on older
 *             browsers — content stays crawlable either way).
 *   className
 */
function FaqItem({ item, name }) {
  return (
    <details className={s.item} name={name}>
      <summary className={s.summary}>
        <span className={s.q}>{item.q}</span>
        <span className={s.icon} aria-hidden="true">
          <Plus size={16} strokeWidth={2.4} />
        </span>
      </summary>
      <div className={s.answer}>{item.a}</div>
    </details>
  );
}

export function FaqAccordion({ items = [], columns = 1, name, className = '' }) {
  if (columns === 2) {
    const mid = Math.ceil(items.length / 2);
    const groups = [items.slice(0, mid), items.slice(mid)];
    return (
      <div className={clsx(s.grid2, className)}>
        {groups.map((group, c) => (
          <div className={s.col} key={c}>
            {group.map((item, i) => (
              <FaqItem key={i} item={item} name={name} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={clsx(s.col, className)}>
      {items.map((item, i) => (
        <FaqItem key={i} item={item} name={name} />
      ))}
    </div>
  );
}
